import {
    useLayoutEffect,
    isValidElement,
    type ReactNode,
    createContext,
    cloneElement,
    useContext,
    useEffect,
    Fragment,
    useState,
    useMemo,
    useRef,
    type FC,
    useId
} from "react";
import {
    type GestureResponderEvent,
    type LayoutChangeEvent,
    useWindowDimensions,
    Text as NativeText,
    type PointerEvent,
    Pressable,
    Animated,
    Platform,
    Image,
    View
} from "react-native";
import type ITooltipProps from "./type";
import type {
    TooltipKeyboardEventType,
    TooltipTriggerPropsType,
    TooltipNestedChildType,
    TooltipNodePropsType,
    TooltipWebPropsType,
    TooltipNestingType,
    TooltipOpenReason,
    TooltipActionType,
    TooltipMode
} from "./type";
import stylesheet, {
    TOOLTIP_INTERACTIVE_PROPS,
    getIsTooltipLocationExact,
    TOOLTIP_TABBABLE_SELECTOR,
    TOOLTIP_EDITABLE_SELECTOR,
    getTooltipActionColors,
    getTooltipInverseTheme,
    TOOLTIP_TITLE_VARIANTS,
    getTooltipIconColor,
    getTooltipPlacement,
    TOOLTIP_IMAGE_ROLES,
    useStyles
} from "./stylesheet";
import TooltipProvider from "./components/provider";
import {
    TooltipDelayContext
} from "./context";
import {
    getIsTooltipHoverSkipped,
    markTooltipHoverClosed,
    markTooltipHoverOpened
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../types";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    useOverlayAccessibility,
    useAnchoredPosition,
    POPOVER_PORTAL_NAME
} from "../../helpers/overlay";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    usePopoverSurfaceMotion
} from "../popoverSurface/hook";
import type {
    AnchoredSizeType
} from "../../helpers/overlay";
import {
    X as XIcon
} from "lucide-react-native";
import {
    getPopoverSurfaceOrigin
} from "../popoverSurface/stylesheet";
import {
    warnOnce
} from "../../utils/development";
import {
    Portal
} from "../../helpers/portalize";
import AnimatedIcon from "../animatedIcon";
import Button from "../button";
import Text from "../text";

const TooltipNestingContext = createContext<TooltipNestingType | null>(null);

const TooltipCloseIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    animation="signature"
    icon={XIcon}
    size={size}
/>;

const getIsTooltipResponder = () => true;

const getIsHoverPointer = (event: PointerEvent) => event.nativeEvent.pointerType !== "touch";

const getTooltipElement = (node: unknown) => {
    if(typeof HTMLElement === "undefined" || !(node instanceof HTMLElement)) {
        return null;
    }

    return node;
};

const TOOLTIP_PASSIVE_TYPES: Array<unknown> = [
    NativeText,
    Fragment,
    Image,
    Text,
    View
];

/**
 * Native has no DOM to inspect, so the anchor may group its child into one accessibility element only when the whole
 * element tree is provably passive: Text, Image, a plain View, or an element declared as an image (role "img").
 * Anything unknown, such as a custom control, counts as interactive so its own accessibility element is never swallowed.
 * Web keeps the lenient check (interactive props only) as the first-render fallback until the DOM is measured.
 */
const getIsTooltipPassiveNode = (node: ReactNode, isStrict: boolean): boolean => {
    if(Array.isArray(node)) {
        return node.every((childNode) => getIsTooltipPassiveNode(childNode, isStrict));
    }

    if(!isValidElement<TooltipNodePropsType>(node)) {
        return true;
    }

    if(TOOLTIP_INTERACTIVE_PROPS.some((propKey) => propKey in node.props)) {
        return false;
    }

    const isImage = TOOLTIP_IMAGE_ROLES.includes(node.props.role ?? node.props.accessibilityRole ?? "");

    if(isStrict && !isImage && !TOOLTIP_PASSIVE_TYPES.includes(node.type)) {
        return false;
    }

    return getIsTooltipPassiveNode(node.props.children, isStrict && !isImage);
};

const getTooltipFocusableElements = (container: HTMLElement) => {
    return Array.from(container.querySelectorAll<HTMLElement>(TOOLTIP_TABBABLE_SELECTOR)).filter((element) => {
        return element.tabIndex >= 0 && !element.matches(":disabled");
    });
};

const getTooltipTabbableElements = (container: HTMLElement) => {
    return getTooltipFocusableElements(container).filter((element) => element.getClientRects().length > 0);
};

const getTooltipTriggerElement = (anchorElement: HTMLElement) => {
    const anchorTabbables = getTooltipTabbableElements(anchorElement);

    const lastElement = anchorTabbables[anchorTabbables.length - 1];

    if(lastElement) {
        return lastElement;
    }

    return anchorElement.tabIndex >= 0 ? anchorElement : null;
};

const getTooltipNextTabbable = (anchorElement: HTMLElement, surfaceElement: HTMLElement) => {
    const documentTabbables = getTooltipTabbableElements(anchorElement.ownerDocument.body).filter((element) => !surfaceElement.contains(element));

    let anchorIndex = -1;

    documentTabbables.forEach((element, index) => {
        if(anchorElement.contains(element)) {
            anchorIndex = index;
        }
    });

    if(anchorIndex === -1) {
        return null;
    }

    return documentTabbables[anchorIndex + 1] ?? null;
};

const focusTooltipElement = (element: HTMLElement) => {
    element.focus({
        preventScroll: true
    });
};

const Tooltip: FC<ITooltipProps> = ({
    isCloseOnPressActionButton = true,
    isShowCloseButton = false,
    icon: IconComponentProp,
    isCloseOnPress = false,
    contentContainerStyle,
    isDefaultOpen = false,
    triggerMode = "hover",
    isOpen: isOpenProp,
    sizing = "content",
    align: alignProp,
    type = "neutral",
    secondaryAction,
    containerStyle,
    customLocalize,
    side: sideProp,
    onOpenChange,
    customTheme,
    caretStyle,
    closeDelay,
    isVisible,
    openDelay,
    children,
    location,
    onClosed,
    subTitle,
    content,
    onClose,
    action,
    height,
    onOpen,
    style,
    title,
    width
}) => {
    const {
        controlPaddings,
        shapeConfigs,
        activeTheme,
        typography,
        durations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const {
        width: windowWidth
    } = useWindowDimensions();

    const pointerType = usePointerType();

    const parentNesting = useContext(TooltipNestingContext);

    const providerDelays = useContext(TooltipDelayContext);

    const tooltipId = useId();

    const [
        isInternalVisible,
        setIsInternalVisible
    ] = useState(isDefaultOpen);

    const [
        openReason,
        setOpenReason
    ] = useState<TooltipOpenReason>("hover");

    const [
        isMounted,
        setIsMounted
    ] = useState(false);

    const [
        surfaceSize,
        setSurfaceSize
    ] = useState<AnchoredSizeType | null>(null);

    const [
        hasFocusableChild,
        setHasFocusableChild
    ] = useState<boolean | null>(null);

    const anchorRef = useRef<View>(null);

    const surfaceRef = useRef<View>(null);

    const isHoverTrackedRef = useRef(false);

    const hoverCloserRef = useRef(() => {
        controllerRef.current.onCloseTimer();
    });

    const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const openChildrenRef = useRef(new Map<string, TooltipNestedChildType>());

    const isTriggerHoveredRef = useRef(false);

    const isSurfaceHoveredRef = useRef(false);

    const wasTriggerFocusedRef = useRef(false);

    const isFocusOpenBlockedRef = useRef(false);

    const surfaceFocusCountRef = useRef(0);

    const controlledOpen = isOpenProp ?? isVisible;

    const isControlled = controlledOpen !== undefined;

    const isShow = isControlled ? controlledOpen : isInternalVisible;

    const isShowRef = useRef(isShow);

    const isRendered = isShow || isMounted;

    const isCloseAction = !!action && !action.title;

    const isPopover = !!action || !!secondaryAction || isShowCloseButton;

    const mode: TooltipMode = isPopover ? "popover" : "plain";

    const isLightDismiss = triggerMode === "click" || openReason === "press";

    const isCoarsePointer = pointerType === "coarse";

    const isLongPressOpen = triggerMode === "hover" && isCoarsePointer;

    const isAnchorPressToggle = triggerMode === "click" || isCoarsePointer;

    const titleId = `${tooltipId}-title`;

    const isTitleRendered = !content && !!title;

    const childElement = isValidElement<TooltipTriggerPropsType>(children) && children.type !== Fragment ? children : null;

    const isPressableChild = !!childElement && "onPress" in childElement.props;

    const isPassiveChild = getIsTooltipPassiveNode(children, Platform.OS !== "web");

    const isAnchorFocusTarget = hasFocusableChild === null ? Platform.OS !== "web" && isPassiveChild : !hasFocusableChild;

    const isAnchorPressable = isAnchorPressToggle || (isAnchorFocusTarget && isPopover);

    const accessibilityHint = content ? "" : [
        title,
        subTitle
    ].filter((part) => !!part).join(", ");

    const placement = getTooltipPlacement({
        align: alignProp,
        side: sideProp,
        location
    });

    const inverseTheme = getTooltipInverseTheme({
        customTheme,
        activeTheme
    });

    const {
        onContentLayout,
        isPositioned,
        actualAlign,
        arrowOffset,
        actualSide,
        left,
        top
    } = useAnchoredPosition({
        align: placement.align,
        isActive: isRendered,
        side: placement.side,
        customTheme,
        anchorRef
    });

    const handleExited = () => {
        isSurfaceHoveredRef.current = false;

        surfaceFocusCountRef.current = 0;

        setIsMounted(false);

        onClosed?.();
    };

    const {
        opacity,
        motion
    } = usePopoverSurfaceMotion({
        onExited: handleExited,
        isReady: isPositioned,
        isOpen: isShow,
        customTheme,
        actualSide,
        isRendered
    });

    const {
        isFocusVisible: isTriggerFocusVisible,
        isFocused: isTriggerFocused,
        interactionHandlers
    } = useInteractionState({
        onFocus: childElement?.props.onFocus,
        onBlur: childElement?.props.onBlur
    });

    const isAnchorFocusVisible = isAnchorFocusTarget && isTriggerFocusVisible;

    const clearOpenTimer = () => {
        if(!openTimerRef.current) {
            return;
        }

        clearTimeout(openTimerRef.current);

        openTimerRef.current = null;
    };

    const clearCloseTimer = () => {
        if(!closeTimerRef.current) {
            return;
        }

        clearTimeout(closeTimerRef.current);

        closeTimerRef.current = null;
    };

    const requestOpen = (reason: TooltipOpenReason) => {
        clearOpenTimer();

        clearCloseTimer();

        if(isShowRef.current) {
            return;
        }

        setOpenReason(reason);

        if(!isControlled) {
            isShowRef.current = true;

            if(reason === "hover") {
                isHoverTrackedRef.current = true;

                markTooltipHoverOpened(hoverCloserRef.current);
            }

            setIsInternalVisible(true);
        }

        onOpenChange?.(true);

        onOpen?.();
    };

    const isFocusInsideSurface = () => {
        if(Platform.OS !== "web") {
            return surfaceFocusCountRef.current > 0;
        }

        const surfaceElement = getTooltipElement(surfaceRef.current);

        const activeElement = surfaceElement ? surfaceElement.ownerDocument.activeElement : null;

        return !!surfaceElement && !!activeElement && surfaceElement.contains(activeElement);
    };

    const focusSurface = () => {
        const surfaceElement = getTooltipElement(surfaceRef.current);

        if(!surfaceElement) {
            return false;
        }

        const [
            firstElement
        ] = getTooltipTabbableElements(surfaceElement);

        focusTooltipElement(firstElement ?? surfaceElement);

        return true;
    };

    const returnFocusToTrigger = () => {
        const anchorElement = getTooltipElement(anchorRef.current);

        if(!anchorElement || !isFocusInsideSurface()) {
            return;
        }

        const triggerElement = getTooltipTriggerElement(anchorElement);

        if(!triggerElement) {
            return;
        }

        focusTooltipElement(triggerElement);

        isFocusOpenBlockedRef.current = triggerElement.ownerDocument.activeElement === triggerElement;
    };

    const requestClose = () => {
        clearOpenTimer();

        clearCloseTimer();

        if(!isShowRef.current) {
            return;
        }

        returnFocusToTrigger();

        if(isHoverTrackedRef.current) {
            isHoverTrackedRef.current = false;

            markTooltipHoverClosed(hoverCloserRef.current);
        }

        if(!isControlled) {
            isShowRef.current = false;

            setIsInternalVisible(false);
        }

        onOpenChange?.(false);

        onClose?.();
    };

    const requestToggle = (reason: TooltipOpenReason) => {
        if(isShowRef.current) {
            requestClose();

            return;
        }

        requestOpen(reason);
    };

    const onCloseTimer = () => {
        if(
            isTriggerHoveredRef.current ||
            isSurfaceHoveredRef.current ||
            openChildrenRef.current.size ||
            isTriggerFocusVisible ||
            isFocusInsideSurface()
        ) {
            return;
        }

        requestClose();
    };

    const onFocusLeave = (isFocusMoved: boolean) => {
        if(triggerMode === "hover" && openReason !== "press") {
            if(isTriggerHoveredRef.current || isSurfaceHoveredRef.current || openChildrenRef.current.size) {
                return;
            }

            requestClose();

            return;
        }

        if(isPopover && isFocusMoved) {
            requestClose();
        }
    };

    const scheduleOpen = () => {
        clearCloseTimer();

        if(isShowRef.current || openTimerRef.current) {
            return;
        }

        const skipDelay = providerDelays?.skipDelay ?? durations.hoverClose;

        const delay = !isControlled && getIsTooltipHoverSkipped(skipDelay) ? 0 : openDelay ?? providerDelays?.openDelay ?? durations.hoverIntent;

        if(delay <= 0) {
            controllerRef.current.requestOpen("hover");

            return;
        }

        openTimerRef.current = setTimeout(() => {
            openTimerRef.current = null;

            controllerRef.current.requestOpen("hover");
        }, delay);
    };

    const scheduleClose = () => {
        clearOpenTimer();

        if(!isShowRef.current || triggerMode !== "hover" || openReason === "press" || closeTimerRef.current) {
            return;
        }

        closeTimerRef.current = setTimeout(() => {
            closeTimerRef.current = null;

            controllerRef.current.onCloseTimer();
        }, closeDelay ?? providerDelays?.closeDelay ?? durations.hoverClose);
    };

    const isContainingTarget = (target: Node): boolean => {
        const anchorElement = getTooltipElement(anchorRef.current);

        const surfaceElement = getTooltipElement(surfaceRef.current);

        if(anchorElement?.contains(target) || surfaceElement?.contains(target)) {
            return true;
        }

        return Array.from(openChildrenRef.current.values()).some((child) => child.isContainingTarget(target));
    };

    const controllerRef = useRef({
        isContainingTarget,
        scheduleClose,
        onCloseTimer,
        onFocusLeave,
        requestClose,
        requestOpen
    });

    const nestingValue = useMemo<TooltipNestingType>(() => ({
        onChildChange: (childKey, child) => {
            if(child) {
                openChildrenRef.current.set(childKey, child);

                return;
            }

            if(!openChildrenRef.current.delete(childKey) || openChildrenRef.current.size) {
                return;
            }

            if(isTriggerHoveredRef.current || isSurfaceHoveredRef.current) {
                return;
            }

            controllerRef.current.scheduleClose();
        }
    }), []);

    const {
        containerProps
    } = useOverlayAccessibility({
        isAutoFocused: isPopover && openReason === "press",
        containerRef: surfaceRef,
        onDismiss: requestClose,
        isActive: isShow,
        isModal: false
    });

    const {
        textContainer: textContainerDynamicStyle,
        closeButton: closeButtonDynamicStyle,
        actions: actionsDynamicStyle,
        content: contentDynamicStyle,
        surface: surfaceDynamicStyle,
        anchor: anchorDynamicStyle,
        caret: caretDynamicStyle,
        icon: iconDynamicStyle
    } = useStyles({
        isShow: isShow && isPositioned,
        isAnchorFocusVisible,
        isAnchorFocusTarget,
        isAnchorPressable,
        controlPaddings,
        isCloseOnPress,
        shapeConfigs,
        arrowOffset,
        pointerType,
        surfaceSize,
        triggerMode,
        windowWidth,
        actualSide,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        height,
        sizing,
        spaces,
        width,
        mode
    });

    useLayoutEffect(() => {
        isShowRef.current = isShow;

        controllerRef.current = {
            isContainingTarget,
            scheduleClose,
            onCloseTimer,
            onFocusLeave,
            requestClose,
            requestOpen
        };
    });

    useLayoutEffect(() => {
        const anchorElement = getTooltipElement(anchorRef.current);

        if(!anchorElement) {
            return;
        }

        setHasFocusableChild(getTooltipFocusableElements(anchorElement).length > 0);
    });

    useEffect(() => {
        if(isControlled) {
            setIsInternalVisible(false);
        }
    }, [
        isControlled
    ]);

    useEffect(() => {
        if(isShow || !isHoverTrackedRef.current) {
            return;
        }

        isHoverTrackedRef.current = false;

        markTooltipHoverClosed(hoverCloserRef.current);
    }, [
        isShow
    ]);

    useEffect(() => {
        return () => {
            if(openTimerRef.current) {
                clearTimeout(openTimerRef.current);
            }

            if(closeTimerRef.current) {
                clearTimeout(closeTimerRef.current);
            }

            if(isHoverTrackedRef.current) {
                isHoverTrackedRef.current = false;

                markTooltipHoverClosed(hoverCloserRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if(isVisible === undefined) {
            return;
        }

        warnOnce({
            message: "Tooltip: \"isVisible\" is deprecated and will be removed in the next major version. Use \"isOpen\" instead."
        });
    }, [
        isVisible
    ]);

    useEffect(() => {
        if(onOpen === undefined) {
            return;
        }

        warnOnce({
            message: "Tooltip: \"onOpen\" is deprecated and will be removed in the next major version. Use \"onOpenChange\" instead."
        });
    }, [
        onOpen
    ]);

    useEffect(() => {
        if(onClose === undefined) {
            return;
        }

        warnOnce({
            message: "Tooltip: \"onClose\" is deprecated and will be removed in the next major version. Use \"onOpenChange\" instead."
        });
    }, [
        onClose
    ]);

    useEffect(() => {
        if(!location) {
            return;
        }

        warnOnce({
            message: "Tooltip: \"location\" is deprecated and will be removed in the next major version. Use \"side\" and \"align\" instead."
        });

        if(getIsTooltipLocationExact({
            location
        })) {
            return;
        }

        warnOnce({
            message: `Tooltip: "location" ${location.vertical} / ${location.horizontal} has no exact "side" / "align" equivalent; it is shown with side "${placement.side}" and align "${placement.align}".`
        });
    }, [
        location?.horizontal,
        location?.vertical
    ]);

    useEffect(() => {
        if(isShow) {
            setIsMounted(true);
        }
    }, [
        isShow
    ]);

    useEffect(() => {
        const wasTriggerFocused = wasTriggerFocusedRef.current;

        wasTriggerFocusedRef.current = isTriggerFocused;

        if(!isTriggerFocused) {
            isFocusOpenBlockedRef.current = false;
        }

        if(triggerMode !== "hover") {
            return;
        }

        if(isTriggerFocusVisible) {
            if(!isFocusOpenBlockedRef.current) {
                requestOpen("focus");
            }

            return;
        }

        if(Platform.OS === "web" || !wasTriggerFocused || isTriggerFocused || openReason === "press") {
            return;
        }

        scheduleClose();
    }, [
        isTriggerFocusVisible,
        isTriggerFocused
    ]);

    useEffect(() => {
        if(isShow) {
            return;
        }

        returnFocusToTrigger();
    }, [
        isShow
    ]);

    useEffect(() => {
        if(Platform.OS !== "web" || typeof globalThis.document === "undefined" || !isShow) {
            return;
        }

        const webDocument = globalThis.document;

        const onDocumentFocusOut = (event: FocusEvent) => {
            if(!(event.target instanceof Node) || !controllerRef.current.isContainingTarget(event.target)) {
                return;
            }

            const nextTarget = event.relatedTarget instanceof Element ? event.relatedTarget : null;

            if(nextTarget && controllerRef.current.isContainingTarget(nextTarget)) {
                return;
            }

            controllerRef.current.onFocusLeave(nextTarget !== null);
        };

        webDocument.addEventListener("focusout", onDocumentFocusOut);

        return () => {
            webDocument.removeEventListener("focusout", onDocumentFocusOut);
        };
    }, [
        isShow
    ]);

    useEffect(() => {
        if(!parentNesting || !isShow) {
            return;
        }

        parentNesting.onChildChange(tooltipId, {
            isContainingTarget: (target) => controllerRef.current.isContainingTarget(target)
        });

        return () => {
            parentNesting.onChildChange(tooltipId, null);
        };
    }, [
        parentNesting,
        isShow
    ]);

    useEffect(() => {
        if(Platform.OS !== "web" || typeof globalThis.document === "undefined" || !isShow || !isLightDismiss) {
            return;
        }

        const webDocument = globalThis.document;

        const onDocumentPointerDown = (event: Event) => {
            if(event.target instanceof Node && controllerRef.current.isContainingTarget(event.target)) {
                return;
            }

            controllerRef.current.requestClose();
        };

        webDocument.addEventListener("pointerdown", onDocumentPointerDown, true);

        return () => {
            webDocument.removeEventListener("pointerdown", onDocumentPointerDown, true);
        };
    }, [
        isLightDismiss,
        isShow
    ]);

    const onTriggerPress = (event: GestureResponderEvent) => {
        childElement?.props.onPress?.(event);

        requestToggle("press");
    };

    const onTriggerLongPress = (event: GestureResponderEvent) => {
        childElement?.props.onLongPress?.(event);

        requestOpen("press");
    };

    const onAnchorPress = () => {
        if(isAnchorPressToggle) {
            requestToggle("press");

            return;
        }

        if(isShowRef.current) {
            focusSurface();

            return;
        }

        requestOpen("press");
    };

    const onAnchorKeyDown = (event: TooltipKeyboardEventType) => {
        const anchorElement = getTooltipElement(anchorRef.current);

        const targetElement = getTooltipElement(event.target);

        if(!isPopover || !anchorElement || !targetElement) {
            return;
        }

        if(event.key === "ArrowDown") {
            if(targetElement.matches(TOOLTIP_EDITABLE_SELECTOR)) {
                return;
            }

            event.preventDefault();

            if(isShowRef.current) {
                focusSurface();

                return;
            }

            requestOpen("press");

            return;
        }

        if(event.key !== "Tab" || event.shiftKey || !isShowRef.current || getTooltipTriggerElement(anchorElement) !== targetElement) {
            return;
        }

        if(focusSurface()) {
            event.preventDefault();
        }
    };

    const onSurfaceKeyDown = (event: TooltipKeyboardEventType) => {
        const surfaceElement = getTooltipElement(surfaceRef.current);

        const anchorElement = getTooltipElement(anchorRef.current);

        const targetElement = getTooltipElement(event.target);

        if(event.key !== "Tab" || !isPopover || !surfaceElement || !anchorElement || !targetElement) {
            return;
        }

        const surfaceTabbables = getTooltipTabbableElements(surfaceElement);

        if(event.shiftKey) {
            const triggerElement = getTooltipTriggerElement(anchorElement);

            if(!triggerElement || (targetElement !== surfaceElement && targetElement !== surfaceTabbables[0])) {
                return;
            }

            event.preventDefault();

            focusTooltipElement(triggerElement);

            return;
        }

        const isLastElement = surfaceTabbables.length ? targetElement === surfaceTabbables[surfaceTabbables.length - 1] : targetElement === surfaceElement;

        const nextElement = isLastElement ? getTooltipNextTabbable(anchorElement, surfaceElement) : null;

        if(!nextElement) {
            return;
        }

        event.preventDefault();

        focusTooltipElement(nextElement);
    };

    const onSurfaceButtonFocus = () => {
        surfaceFocusCountRef.current += 1;
    };

    const onSurfaceButtonBlur = () => {
        surfaceFocusCountRef.current = Math.max(0, surfaceFocusCountRef.current - 1);

        if(Platform.OS !== "web") {
            scheduleClose();
        }
    };

    const onAnchorLongPress = () => {
        requestOpen("press");
    };

    const onAnchorPointerEnter = (event: PointerEvent) => {
        if(triggerMode !== "hover" || !getIsHoverPointer(event)) {
            return;
        }

        isTriggerHoveredRef.current = true;

        scheduleOpen();
    };

    const onAnchorPointerLeave = (event: PointerEvent) => {
        if(triggerMode !== "hover" || !getIsHoverPointer(event)) {
            return;
        }

        isTriggerHoveredRef.current = false;

        scheduleClose();
    };

    const onSurfacePointerEnter = (event: PointerEvent) => {
        if(!getIsHoverPointer(event)) {
            return;
        }

        isSurfaceHoveredRef.current = true;

        clearCloseTimer();
    };

    const onSurfacePointerLeave = (event: PointerEvent) => {
        if(!getIsHoverPointer(event)) {
            return;
        }

        isSurfaceHoveredRef.current = false;

        scheduleClose();
    };

    const onSurfaceLayout = (event: LayoutChangeEvent) => {
        onContentLayout(event);

        const {
            height: surfaceHeight,
            width: surfaceWidth
        } = event.nativeEvent.layout;

        setSurfaceSize((previousSize) => {
            if(previousSize && previousSize.height === surfaceHeight && previousSize.width === surfaceWidth) {
                return previousSize;
            }

            return {
                height: surfaceHeight,
                width: surfaceWidth
            };
        });
    };

    const onActionPress = (actionItem: TooltipActionType) => {
        if(isCloseOnPressActionButton) {
            requestClose();
        }

        actionItem.onPress({
            closeAnimation: () => controllerRef.current.requestClose()
        });
    };

    const onClosePress = () => {
        if(isShowCloseButton) {
            requestClose();
        }

        if(action && isCloseAction) {
            onActionPress(action);
        }
    };

    const renderIcon = () => {
        if(!IconComponentProp) {
            return null;
        }

        return <View
            style={iconDynamicStyle}
            aria-hidden={true}
        >
            <IconComponentProp
                customColor={getTooltipIconColor({
                    colors,
                    type
                })}
                size={iconSizes.small}
                color="contrastHigh"
            />
        </View>;
    };

    const renderTexts = () => {
        if(!title && !subTitle) {
            return null;
        }

        return <View
            style={[
                isPopover ? stylesheet.popoverTextContainer : stylesheet.textContainer,
                textContainerDynamicStyle,
                contentContainerStyle
            ]}
        >
            {
                title ?
                    <Text
                        variant={TOOLTIP_TITLE_VARIANTS[mode]}
                        customTheme={customTheme}
                        color="contrastHigh"
                        id={titleId}
                    >
                        {title}
                    </Text>
                :
                    null
            }
            {
                subTitle ?
                    <Text
                        customTheme={customTheme}
                        variant="bodySmallSize"
                        color="contrastMid"
                    >
                        {subTitle}
                    </Text>
                :
                    null
            }
        </View>;
    };

    const renderContent = () => {
        if(!content) {
            return renderTexts();
        }

        if(typeof content === "function") {
            return content();
        }

        return content;
    };

    const renderCloseButton = () => {
        if(!isShowCloseButton && !isCloseAction) {
            return null;
        }

        return <Button
            customIconColor={colors.content.text.contrastHigh}
            accessibilityLabel={localize("close")}
            style={closeButtonDynamicStyle}
            onFocus={onSurfaceButtonFocus}
            onBlur={onSurfaceButtonBlur}
            customTheme={inverseTheme}
            icon={TooltipCloseIcon}
            onPress={onClosePress}
            spreadBehaviour="free"
            variant="ghost"
            type="neutral"
            size="small"
        />;
    };

    const renderActionButton = (actionItem: TooltipActionType, isPrimary: boolean) => {
        const actionColors = getTooltipActionColors({
            isPrimary,
            colors
        });

        return <Button
            customBorderColor={actionColors.customBorderColor}
            customTextColor={actionColors.customTextColor}
            onPress={() => onActionPress(actionItem)}
            type={isPrimary ? "primary" : "neutral"}
            variant={isPrimary ? "tonal" : "ghost"}
            customColor={actionColors.customColor}
            onFocus={onSurfaceButtonFocus}
            onBlur={onSurfaceButtonBlur}
            customTheme={inverseTheme}
            title={actionItem.title}
            style={actionItem.style}
            icon={actionItem.icon}
            spreadBehaviour="free"
            size="small"
        />;
    };

    const renderActions = () => {
        const primaryAction = action && action.title ? action : undefined;

        if(!primaryAction && !secondaryAction) {
            return null;
        }

        return <View
            style={[
                stylesheet.actions,
                actionsDynamicStyle
            ]}
        >
            {primaryAction ? renderActionButton(primaryAction, true) : null}
            {secondaryAction ? renderActionButton(secondaryAction, false) : null}
        </View>;
    };

    const renderSurface = () => {
        const surfaceWebProps: TooltipWebPropsType = Platform.OS === "web" ? {
            onKeyDown: onSurfaceKeyDown
        } : {};

        return <Animated.View
            {...containerProps}
            {...surfaceWebProps}
            onStartShouldSetResponder={isCloseOnPress ? getIsTooltipResponder : undefined}
            aria-labelledby={isPopover && isTitleRendered ? titleId : undefined}
            onResponderRelease={isCloseOnPress ? requestClose : undefined}
            style={[
                stylesheet.surface,
                surfaceDynamicStyle,
                {
                    transformOrigin: getPopoverSurfaceOrigin({
                        actualAlign,
                        actualSide,
                        direction
                    }),
                    transform: [
                        {
                            translateX: left
                        },
                        {
                            translateY: top
                        },
                        {
                            translateX: motion.translateX
                        },
                        {
                            translateY: motion.translateY
                        },
                        {
                            scale: motion.scale
                        }
                    ],
                    opacity
                },
                containerStyle
            ]}
            role={isPopover ? "dialog" : "tooltip"}
            onPointerEnter={onSurfacePointerEnter}
            onPointerLeave={onSurfacePointerLeave}
            onLayout={onSurfaceLayout}
            ref={surfaceRef}
            id={tooltipId}
        >
            <View
                style={[
                    stylesheet.caret,
                    caretDynamicStyle,
                    caretStyle
                ]}
                aria-hidden={true}
            />
            <View
                style={[
                    stylesheet.content,
                    contentDynamicStyle
                ]}
            >
                {renderIcon()}
                {renderContent()}
                {renderCloseButton()}
            </View>
            {renderActions()}
        </Animated.View>;
    };

    const renderBackdrop = () => {
        if(Platform.OS === "web" || !isShow || !isLightDismiss) {
            return null;
        }

        return <Pressable
            importantForAccessibility="no"
            style={stylesheet.backdrop}
            onPress={requestClose}
            accessible={false}
        />;
    };

    const renderPortal = () => {
        if(!isRendered) {
            return null;
        }

        return <Portal
            name={POPOVER_PORTAL_NAME}
        >
            <TooltipNestingContext.Provider
                value={nestingValue}
            >
                {renderBackdrop()}
                {renderSurface()}
            </TooltipNestingContext.Provider>
        </Portal>;
    };

    const renderTrigger = () => {
        if(!childElement) {
            return children;
        }

        const triggerProps: TooltipTriggerPropsType = {};

        if(isPressableChild && isLongPressOpen) {
            triggerProps.onLongPress = onTriggerLongPress;
        }

        if(isPressableChild && triggerMode === "click") {
            triggerProps.onPress = onTriggerPress;
        }

        if(isAnchorFocusTarget) {
            return cloneElement(childElement, triggerProps);
        }

        triggerProps.onFocus = interactionHandlers.onFocus;

        triggerProps.onBlur = interactionHandlers.onBlur;

        if(Platform.OS === "web" && isShow && !isPopover) {
            triggerProps["aria-describedby"] = [
                childElement.props["aria-describedby"],
                tooltipId
            ].filter((part) => !!part).join(" ");
        }

        if(Platform.OS !== "web" && !isPopover && accessibilityHint && !childElement.props.accessibilityHint) {
            triggerProps.accessibilityHint = accessibilityHint;
        }

        if(isPopover) {
            triggerProps["aria-expanded"] = isShow;
        }

        return cloneElement(childElement, triggerProps);
    };

    const anchorWebProps: TooltipWebPropsType = Platform.OS === "web" ? {
        "aria-describedby": isAnchorFocusTarget && isShow && !isPopover ? tooltipId : undefined,
        dataSet: isAnchorFocusTarget ? interactionHandlers.dataSet : undefined,
        onKeyDown: onAnchorKeyDown
    } : {};

    return <View
        style={style}
    >
        <Pressable
            {...anchorWebProps}
            accessibilityHint={Platform.OS !== "web" && isAnchorFocusTarget && !isPopover && accessibilityHint ? accessibilityHint : undefined}
            onFocus={isAnchorFocusTarget ? interactionHandlers.onFocus : undefined}
            role={isAnchorFocusTarget && isAnchorPressable ? "button" : undefined}
            aria-expanded={isPopover && isAnchorFocusTarget ? isShow : undefined}
            onBlur={isAnchorFocusTarget ? interactionHandlers.onBlur : undefined}
            onLongPress={isLongPressOpen ? onAnchorLongPress : undefined}
            onPress={isAnchorPressable ? onAnchorPress : undefined}
            tabIndex={isAnchorFocusTarget ? 0 : -1}
            onPointerEnter={onAnchorPointerEnter}
            onPointerLeave={onAnchorPointerLeave}
            accessible={isAnchorFocusTarget}
            style={[
                stylesheet.anchor,
                anchorDynamicStyle
            ]}
            ref={anchorRef}
        >
            {renderTrigger()}
            {renderPortal()}
        </Pressable>
    </View>;
};
export default Object.assign(Tooltip, {
    Provider: TooltipProvider
});
